import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useUser } from '../providers/UserProvider';
import type { StoryElement } from '../types';
import { ReaderNavbar } from '../components/ReaderNavbar';
import { setSavedSearchPresence } from '../utils/savedSearchPresence';
import { IconChevron } from '../components/icons';
import { ReaderFooter } from '../components/ReaderFooter';
import { TldrSubscribeBand } from '../components/TldrSubscribeBand';
import { Toast } from '../components/Toast';
import { StoryList } from '../components/StoryList';
import { StoryRow } from '../components/StoryRow';
import { useStoryKeyboardNav } from '../hooks/useStoryKeyboardNav';
import { useSearchStopwatch } from '../hooks/useSearchStopwatch';
import { formatStopwatchSeconds, stopwatchReading, stopwatchTitle } from '../utils/searchStopwatch';
import type { SearchStopwatchRun } from '../utils/searchStopwatch';
import { ApiError } from '../api';
import * as channelRepository from '../repositories/channelRepository';
import {
  createSavedSearch,
  getSavedSearch,
  getSearchRunStatus,
  runSearch,
  getSearchDescription,
  listSavedSearches,
  listRecentSearches,
  updateSavedSearch,
  getTopSavedSearches,
  type SavedSearch,
  type RecentSearch,
  type JudgeCover,
  type SearchTimingDetails,
  type TopSavedSearch,
} from '../repositories/savedSearchRepository';
import {
  getTldrPrompt,
  getTldrPreviewStatus,
  startTldrPreview,
} from '../repositories/tldrPromptRepository';
import {
  DECAY_MAX,
  DECAY_MIN,
  DECAY_SLIDER_STEP,
  formatDecay,
  formatRelevanceMultiplier,
  DEFAULT_DECAY,
  DEFAULT_RELEVANCE_MULTIPLIER as DEFAULT_RELEVANCE,
  RELEVANCE_MULT_MAX,
  RELEVANCE_MULT_MIN,
  RELEVANCE_SLIDER_STEP,
  SAVED_SEARCH_DEFAULT_DECAY,
  SAVED_SEARCH_DEFAULT_RELEVANCE,
} from '../scoringConfig';
import {
  READING_MINUTES_DEFAULT,
  READING_MINUTES_MAX,
  READING_MINUTES_MIN,
} from '../readingMinutesSpec';
import { FLAGGED_CHANNEL_ID, NEW_SEARCH_CHANNEL_ID, savedSearchChannelId, savedSearchIdFromChannel, TLDR_CHANNEL_ID } from '../utils/channelUrl';
import { rememberSelectedChannel, rememberSelectedSavedSearch } from '../utils/selectedChannel';
import { searchWindowAction, narrowToSelectedWindow, runWindowFromStatus } from '../utils/searchWindow';
import type { SearchRunWindow } from '../utils/searchWindow';
import {
  describeActiveFilters,
  refreshingLabel,
  searchingLabel,
  searchSnapshot,
  searchTimelineDurationMs,
} from '../utils/searchFilters';
import { ALL_CATEGORIES, CATEGORY_LABELS } from '../utils/storyType';
import type { SearchPollPoint, SearchRunProgress } from '../utils/searchFilters';
import { formatAge } from '../utils/formatTime';
import { isRunFinished } from '../utils/savedSearchRunState';
import {
  APPLY_BATCH_MARKER,
  CUT_OFF_BATCH_MARKER,
  LANE_DETAILS,
  LLM_BATCH_MARKER,
  PROGRESS_FLUSH_PREFIX,
  timingSpanDetail,
  WAVE_FALLBACK_NAME,
  WAVE_NAMES,
  type TimingCategory,
} from '../utils/searchTiming';
import { useSearchRunFollower } from '../hooks/useSearchRunFollower';

const MAX_VISIBLE_PILLS = 3;

interface SearchFilters {
  excludeCategories: string[];
  readingMinutesMin: number | null;
  readingMinutesMax: number | null;
}

interface SearchSubmitValues {
  description: string;
  decay_exponent: number;
  relevance_boost_multiplier: number;
  filters: SearchFilters;
  fast_search_window_hours?: 24 | 72;
  expand_query?: boolean;
}

interface SearchSaveValues extends SearchSubmitValues {
  name: string;
  isTldrPreview: boolean;
  feedEnabled: boolean;
}

/** Composer prefill built from a persisted search run (e.g. a bookmarked run URL). */
interface RunPrefill {
  searchId: string;
  description: string;
  decay: number;
  relevance: number;
  isTldrPreview: boolean;
}

function TopSearchPills({
  topSearches,
  allSearches,
  recentSearches,
  onSelect,
  trailing,
  compact,
}: {
  topSearches: TopSavedSearch[];
  allSearches: SavedSearch[];
  recentSearches: RecentSearch[];
  onSelect: (id: string) => void;
  trailing?: ReactNode;
  /** Collapse everything into a single "My feeds" dropdown (post-search). */
  compact?: boolean;
}) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen && !recentOpen) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOverflowOpen(false);
        setRecentOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [overflowOpen, recentOpen]);

  if (topSearches.length === 0 && allSearches.length === 0 && recentSearches.length === 0 && !trailing) return null;

  const visibleIds = new Set(topSearches.slice(0, MAX_VISIBLE_PILLS).map((s) => s.id));
  const visible = topSearches.slice(0, MAX_VISIBLE_PILLS);
  const overflow = allSearches
    .filter((s) => !visibleIds.has(s.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  if (compact) {
    const all: Array<{ id: string; name: string }> = [...visible, ...overflow];
    return (
      <div className="search-pills-compact" ref={dropdownRef}>
        {recentSearches.length > 0 && (
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              type="button"
              className="search-pill search-pill-overflow"
              onClick={() => { setRecentOpen((v) => !v); setOverflowOpen(false); }}
              aria-expanded={recentOpen}
            >
              Recent searches
            </button>
            {recentOpen && (
              <div className="search-pill-dropdown search-pill-dropdown-right">
                {recentSearches.map((search) => (
                  <button
                    key={search.search_id}
                    className="search-pill-dropdown-item"
                    onClick={() => { onSelect(search.description_id); setRecentOpen(false); }}
                    title={search.description}
                  >
                    {search.title || search.description}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {all.length > 0 && (
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              type="button"
              className="search-pill search-pill-overflow"
              onClick={() => { setOverflowOpen((v) => !v); setRecentOpen(false); }}
            >
              My feeds
              <span style={{ marginLeft: 4, display: 'inline-flex' }}>
                <IconChevron open={overflowOpen} />
              </span>
            </button>
            {overflowOpen && (
              <div className="search-pill-dropdown search-pill-dropdown-right">
                {all.map((s) => (
                  <button
                    key={s.id}
                    className="search-pill-dropdown-item"
                    onClick={() => { onSelect(s.id); setOverflowOpen(false); }}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {trailing}
      </div>
    );
  }

  return (
    <div className="search-pills-row" ref={dropdownRef}>
      {trailing}
      {recentSearches.length > 0 && (
        <div style={{ position: 'relative', display: 'inline-flex' }}>
          <button type="button" className="search-pill search-pill-overflow" onClick={() => setRecentOpen((v) => !v)} aria-expanded={recentOpen}>
            Recent searches
          </button>
          {recentOpen && (
            <div className="search-pill-dropdown">
              {recentSearches.map((search) => (
                <button key={search.search_id} className="search-pill-dropdown-item" onClick={() => { onSelect(search.description_id); setRecentOpen(false); }} title={search.description}>
                  {search.title || search.description}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {visible.map((s) => (
        <button
          key={s.id}
          type="button"
          className="search-pill"
          onClick={() => onSelect(s.id)}
          title={s.description}
        >
          {s.name}
        </button>
      ))}
      {overflow.length > 0 && (
        <div style={{ position: 'relative', display: 'inline-flex' }} ref={dropdownRef}>
          <button
            type="button"
            className="search-pill search-pill-overflow"
            onClick={() => setOverflowOpen((v) => !v)}
          >
            Other feeds
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 4 }}>
              <path d={overflowOpen ? 'M2 6.5 L5 3.5 L8 6.5' : 'M2 3.5 L5 6.5 L8 3.5'} />
            </svg>
          </button>
          {overflowOpen && (
            <div className="search-pill-dropdown">
              {overflow.map((s) => (
                <button
                  key={s.id}
                  className="search-pill-dropdown-item"
                  onClick={() => { onSelect(s.id); setOverflowOpen(false); }}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryFilterPills({
  excluded,
  onChange,
  disabled,
}: {
  excluded: string[];
  onChange: (excluded: string[]) => void;
  disabled: boolean;
}) {
  const excludedSet = new Set(excluded);
  const toggle = (cat: string) => {
    if (excludedSet.has(cat)) {
      onChange(excluded.filter((c) => c !== cat));
    } else {
      onChange([...excluded, cat]);
    }
  };
  return (
    <div className="filter-pills-row">
      <span className="filter-pills-label">Categories</span>
      {ALL_CATEGORIES.map((cat) => (
        <button
          key={cat}
          type="button"
          className={`filter-pill${excludedSet.has(cat) ? ' excluded' : ''}`}
          onClick={() => toggle(cat)}
          disabled={disabled}
          title={excludedSet.has(cat) ? `Show ${CATEGORY_LABELS[cat]}` : `Hide ${CATEGORY_LABELS[cat]}`}
        >
          {excludedSet.has(cat) && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" style={{ marginRight: 3, opacity: 0.7 }}>
              <line x1="2" y1="2" x2="8" y2="8" />
              <line x1="8" y1="2" x2="2" y2="8" />
            </svg>
          )}
          {CATEGORY_LABELS[cat]}
        </button>
      ))}
    </div>
  );
}

function ReadingTimeFilter({
  min,
  max,
  onChangeMin,
  onChangeMax,
  disabled,
}: {
  min: number | null;
  max: number | null;
  onChangeMin: (v: number | null) => void;
  onChangeMax: (v: number | null) => void;
  disabled: boolean;
}) {
  return (
    <div className="filter-pills-row">
      <span className="filter-pills-label">Reading time</span>
      <div className="reading-time-controls">
        <select
          value={min != null ? 'min' : max != null ? 'max' : ''}
          onChange={(e) => {
            if (e.target.value === 'min') { onChangeMin(min ?? READING_MINUTES_DEFAULT); onChangeMax(null); }
            else if (e.target.value === 'max') { onChangeMax(max ?? READING_MINUTES_DEFAULT); onChangeMin(null); }
            else { onChangeMin(null); onChangeMax(null); }
          }}
          disabled={disabled}
          className="reading-time-select"
        >
          <option value="">Any</option>
          <option value="max">At most</option>
          <option value="min">At least</option>
        </select>
        {(min != null || max != null) && (
          <>
            <input
              type="number"
              min={READING_MINUTES_MIN}
              max={READING_MINUTES_MAX}
              step={1}
              value={min ?? max ?? READING_MINUTES_DEFAULT}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (isNaN(v) || v < READING_MINUTES_MIN) return;
                if (min != null) onChangeMin(v);
                else onChangeMax(v);
              }}
              disabled={disabled}
              className="reading-time-input"
            />
            <span className="reading-time-unit">min</span>
          </>
        )}
      </div>
    </div>
  );
}

function ActiveFiltersStatus({ filters }: { filters: SearchFilters }) {
  const parts = describeActiveFilters(filters);
  if (parts.length === 0) return null;
  return <span className="search-active-filters">Filters: {parts.join(' · ')}</span>;
}

function formatTiming(ms: number) {
  if (ms >= 1000) return `${(ms / 1000).toFixed(ms >= 10000 ? 1 : 2)}s`;
  if (ms < 0.1) return '<0.1ms';
  if (ms < 10) return `${ms.toFixed(1)}ms`;
  return `${Math.round(ms)}ms`;
}

type TimingSpan = SearchTimingDetails['spans'][number];

interface TimingLane {
  key: string;
  label: string;
  detail: string;
  spans: TimingSpan[];
}

function waveName(value: unknown) {
  return (typeof value === 'string' && WAVE_NAMES[value]) || WAVE_FALLBACK_NAME;
}

function laneIdentity(span: TimingSpan) {
  const wave = span.metadata?.wave;
  const prefix = waveName(wave);
  if (span.key.includes(LLM_BATCH_MARKER)) return { key: `${String(wave)}.llm_calls`, label: `${prefix} LLM calls` };
  if (span.key.includes(CUT_OFF_BATCH_MARKER)) return { key: `${String(wave)}.cut_off`, label: `${prefix} unresolved calls` };
  if (span.key.includes(APPLY_BATCH_MARKER)) return { key: `${String(wave)}.apply`, label: `${prefix} response application` };
  if (span.key.startsWith(PROGRESS_FLUSH_PREFIX)) return { key: 'progress_flushes', label: 'Progress database writes' };
  return { key: span.key, label: span.label };
}

function laneDetail(identity: { key: string }, span: TimingSpan) {
  if (identity.key.endsWith('.llm_calls')) return LANE_DETAILS.llm_calls;
  if (identity.key.endsWith('.cut_off')) return LANE_DETAILS.cut_off;
  if (identity.key.endsWith('.apply')) return LANE_DETAILS.apply;
  if (identity.key === 'progress_flushes') return LANE_DETAILS.progress_flushes;
  return timingSpanDetail(span);
}

function timingLanes(spans: TimingSpan[]): TimingLane[] {
  const lanes = new Map<string, TimingLane>();
  spans.forEach((span) => {
    const identity = laneIdentity(span);
    const existing = lanes.get(identity.key);
    if (existing) {
      existing.spans.push(span);
    } else {
      lanes.set(identity.key, {
        ...identity,
        detail: laneDetail(identity, span),
        spans: [span],
      });
    }
  });
  return [...lanes.values()];
}

// Order and presentation for the fast-search waterfall's simplified view:
// everything before retrieval starts, the query embed, the vector search,
// candidate selection, the remaining backend prep before the LLM wave, the
// LLM wave, and whatever runs after it. Bucket keys are wire contract with
// `_fast_search_latency_buckets` in fast_search_runner.py; a bucket missing
// from a run's payload is skipped, which is what lets the retired
// `embedding` key keep rendering for runs stored before 2026-07-30.
const LATENCY_BUCKETS: { key: string; label: string; category: TimingCategory; detail: string }[] = [
  { key: 'preflight', label: 'Before run start', category: 'setup', detail: 'Server work between the search request arriving and the run existing: resolving the judge context and reading this feed’s editorial overrides. Absent on runs stored before 2026-08-29, when it was measured by nobody and showed up in the stopwatch as network time.' },
  { key: 'setup', label: 'Setup', category: 'setup', detail: 'From when the run started to when retrieval began.' },
  { key: 'embedding', label: 'Embedding + vector search', category: 'retrieval', detail: 'Runs stored before 2026-07-30 combined the query embed and the vector search into one bar.' },
  { key: 'query_embed', label: 'Query embed', category: 'retrieval', detail: 'Embedding the search description. Zero when the description already has a stored vector, which is every repeat run of a saved search.' },
  { key: 'vector_search', label: 'Vector search', category: 'retrieval', detail: 'The pgvector similarity scan that retrieves candidates. Database time, not model time.' },
  { key: 'candidate_selection', label: 'Candidate selection', category: 'retrieval', detail: 'Chooses which retrieved candidates this run judges.' },
  { key: 'misc_backend', label: 'Misc. backend', category: 'retrieval', detail: 'Remaining retrieval arms, cache lookups, and judge-batch preparation before the LLM wave. A sum of several phases, not one span -- click to break it down. Its phases do not all run consecutively: some precede candidate selection.' },
  { key: 'llm_wave', label: 'LLM wave', category: 'judging', detail: 'The single wave of LLM judge calls.' },
  { key: 'after', label: 'After LLM wave', category: 'finalization', detail: 'Aggregating verdicts and writing judgments after the LLM wave finished.' },
];

/**
 * The two judges a run used, named in the timing panel.
 *
 * The panel already showed how long the LLM wave took; which models ran it was
 * only in the backend logs. Now that an admin can repoint the pair, "is this
 * run using what I configured?" is a question the run itself has to answer —
 * a configuration read back from the settings page describes the *next* run,
 * not the one on screen.
 */
function JudgeCoverSummary({ timing }: { timing: SearchTimingDetails }) {
  const covers = timing.judge_covers;
  if (!covers) {
    // Runs stored before the pair became configurable carry only the glued
    // identity. Showing it beats showing nothing, and it is unambiguous.
    if (!timing.judge_model) return null;
    return (
      <div className="search-timing-judges">
        <span className="search-timing-judges-label">Judges</span>
        <span>{timing.judge_model.replaceAll('+', ' + ')}</span>
      </div>
    );
  }
  const rows: { cover: JudgeCover; model: { id: string; label: string } }[] = [
    { cover: 'primary', model: covers.primary },
    { cover: 'hedge', model: covers.hedge },
  ];
  return (
    <div className="search-timing-judges">
      <span className="search-timing-judges-label">Judges</span>
      {rows.map(({ cover, model }) => (
        <span key={cover} title={model.id}>
          {model.label} <span className="search-timing-judges-cover">({cover}</span>
          {covers.authoritative === cover && (
            <span className="search-timing-judges-cover">, authoritative</span>
          )}
          <span className="search-timing-judges-cover">)</span>
        </span>
      ))}
    </div>
  );
}

function SearchTimingWaterfall({
  timing,
  label = 'Search timing',
  compact = false,
}: {
  timing: SearchTimingDetails;
  label?: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  // misc_backend is a sum of eight phases rather than a measured span, so a
  // slow run shows one wide opaque bar. Expanding swaps it for its
  // constituents; it stays collapsed by default because eight more lanes is
  // more than the waterfall reads well at a glance.
  const [miscOpen, setMiscOpen] = useState(false);
  const total = Math.max(1, timing.total_ms);
  // Fast-search rows created before their timing payload joined the standard
  // timing contract have no spans. Treat those persisted payloads as a
  // summary-only waterfall instead of crashing after the final poll.
  const persistedSpans = timing.spans ?? [];
  const bucketTimings = timing.latency_buckets_ms;
  const miscBreakdown = timing.misc_backend_breakdown ?? [];
  const canExpandMisc = miscBreakdown.length > 0;
  const bucketSpans: TimingSpan[] = [];
  let bucketStart = 0;
  if (bucketTimings && typeof bucketTimings === 'object') {
    LATENCY_BUCKETS.forEach(({ key, label: bucketLabel, category, detail }) => {
      const duration = bucketTimings[key];
      if (typeof duration !== 'number') return;
      // Expanded, the bucket's own lane is replaced by one lane per phase. The
      // phases are laid out consecutively inside the width the bucket occupied,
      // which is a proportion view, not a true timeline: neighbor_injection
      // really runs before candidate_selection, above it here. start_ms on each
      // phase carries the real offset for the tooltip.
      if (key === 'misc_backend' && miscOpen && canExpandMisc) {
        miscBreakdown.forEach((part) => {
          bucketSpans.push({
            key: `misc:${part.key}`,
            label: part.key.replaceAll('_', ' '),
            category,
            detail: `Folded into Misc. backend. Started ${formatTiming(part.start_ms)} into the run.`,
            start_ms: bucketStart,
            end_ms: bucketStart + part.duration_ms,
            duration_ms: part.duration_ms,
          });
          bucketStart += part.duration_ms;
        });
        return;
      }
      bucketSpans.push({ key, label: bucketLabel, category, detail, start_ms: bucketStart, end_ms: bucketStart + duration, duration_ms: duration });
      bucketStart += duration;
    });
  }
  const phaseTimings = timing.phase_timings_ms;
  const phaseEntries = phaseTimings && typeof phaseTimings === 'object'
    ? Object.entries(phaseTimings).filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    : [];
  const phaseSpans: TimingSpan[] = [];
  let phaseStart = 0;
  phaseEntries.forEach(([key, duration]) => {
    phaseSpans.push({ key, label: key.replaceAll('_', ' '), category: 'judging', start_ms: phaseStart, end_ms: phaseStart + duration, duration_ms: duration });
    phaseStart += duration;
  });
  const lanes = timingLanes(
    bucketSpans.length > 0 ? bucketSpans : persistedSpans.length > 0 ? persistedSpans : phaseSpans
  );
  return (
    <div className={`search-timing-waterfall${compact ? ' compact' : ''}`}>
      <button type="button" className={compact ? 'search-save-trigger' : 'search-timing-toggle'} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {compact ? formatTiming(timing.total_ms) : `${label} · ${formatTiming(timing.total_ms)}`}
        {!compact && <span className={`search-timing-chevron${open ? ' open' : ''}`} aria-hidden="true">›</span>}
      </button>
      {open && (
        <div className="search-timing-details">
          <div className="search-timing-axis"><span>0</span><span>{formatTiming(timing.total_ms)}</span></div>
          <JudgeCoverSummary timing={timing} />
          {lanes.map((lane) => (
            <div className="search-timing-row" key={lane.key}>
              <span
                className="search-timing-label"
                tabIndex={0}
                data-tooltip={lane.detail}
                aria-label={`${lane.label}: ${lane.detail}`}
                role={lane.key === 'misc_backend' && canExpandMisc ? 'button' : undefined}
                aria-expanded={lane.key === 'misc_backend' && canExpandMisc ? miscOpen : undefined}
                onClick={lane.key === 'misc_backend' && canExpandMisc ? () => setMiscOpen((value) => !value) : undefined}
                onKeyDown={
                  lane.key === 'misc_backend' && canExpandMisc
                    ? (event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        setMiscOpen((value) => !value);
                      }
                    : undefined
                }
              >
                <span className="search-timing-label-text">
                  {lane.label}
                  {lane.key === 'misc_backend' && canExpandMisc && (
                    <span aria-hidden="true">{miscOpen ? ' ▾' : ' ▸'}</span>
                  )}
                </span>
              </span>
              <span className="search-timing-track">
                {lane.spans.map((span) => (
                  <span
                    className={`search-timing-bar ${span.category}`}
                    key={`${span.key}-${span.start_ms}`}
                    title={`${span.label}: ${formatTiming(span.start_ms)}–${formatTiming(span.end_ms)} (${formatTiming(span.duration_ms)})`}
                    style={{ left: `${span.start_ms / total * 100}%`, width: `${Math.max(0.35, span.duration_ms / total * 100)}%` }}
                  />
                ))}
              </span>
              <span className="search-timing-duration">
                {lane.spans.length === 1 ? formatTiming(lane.spans[0].duration_ms) : `${lane.spans.length}×`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * The stopwatch face: counts up from the click, stops on the frame that
 * painted the first results.
 *
 * It owns its own frame loop and its own state so that redrawing the digits
 * re-renders these few characters and nothing else — the composer and the
 * results list around it must not re-render sixty times a second to animate a
 * number. The loop only exists while a run does.
 */
function SearchStopwatchFace({ run, history, startedAtMs, running }: {
  run: SearchStopwatchRun | null;
  history: SearchStopwatchRun[];
  startedAtMs: number;
  running: boolean;
}) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    if (!running || typeof requestAnimationFrame !== 'function') return undefined;
    let frame = requestAnimationFrame(function tick() {
      setElapsedMs(performance.now() - startedAtMs);
      frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [running, startedAtMs]);

  const frozen = stopwatchReading(run);
  // While a run counts, the live clock; once it stops, the mark it earned.
  // Between those two — the couple of frames the mark spends waiting for the
  // paint it is timing — the last tick holds, so the digits never fall back
  // to zero on their way to the answer.
  const shown = running || frozen === null ? elapsedMs : frozen;
  const backgrounded = run !== null && !run.trusted;

  return (
    <span
      className={`search-stopwatch${running ? ' running' : ''}`}
      title={stopwatchTitle(run, history)}
      aria-label="Search timer, click to first painted results"
      role="timer"
    >
      {backgrounded ? '--' : formatStopwatchSeconds(shown)}
    </span>
  );
}

export function shouldShowSearchFeedControls(signedIn: boolean, elementCount: number): boolean {
  return signedIn && elementCount > 0;
}

function SearchPane({
  elements,
  populating,
  submitting,
  error,
  isAdmin,
  signedIn,
  isNew,
  topSearches,
  allSearches,
  recentSearches,
  savedSearch,
  runPrefill,
  restoredAt,
  refreshingRestored,
  runProgress = null,
  pollTimeline = [],
  timingDetails = null,
  currentRunId = null,
  onRerunRestored,
  onSubmit,
  onSave,
  onUpdateSavedSearch,
  onSetFeedEnabled,
  onFeedStatus,
  onSelectSavedSearch,
  onClearSearch,
  onPreviewTldrSearch,
  searchRunWindow,
}: {
  elements: StoryElement[];
  populating: boolean;
  submitting: boolean;
  error: string;
  isAdmin: boolean;
  /** False for anonymous visitors: the composer is visible but cannot run. */
  signedIn: boolean;
  isNew: boolean;
  topSearches: TopSavedSearch[];
  allSearches: SavedSearch[];
  recentSearches: RecentSearch[];
  savedSearch?: SavedSearch | null;
  runPrefill?: RunPrefill | null;
  /** Completion time of the persisted run currently on screen, if restored from cache. */
  restoredAt?: string | null;
  /** True while the restored run's automatic incremental refresh is in flight. */
  refreshingRestored?: boolean;
  /** Latest judged/candidate counters from the active run's status polls. */
  runProgress?: SearchRunProgress | null;
  pollTimeline?: SearchPollPoint[];
  timingDetails?: SearchTimingDetails | null;
  /** The run whose results are on screen, for the admin-only audit link. */
  currentRunId?: string | null;
  onRerunRestored?: () => void;
  onSubmit: (values: SearchSubmitValues) => void | Promise<void>;
  onSave: (values: SearchSaveValues) => void | Promise<void>;
  onUpdateSavedSearch?: (settings: { name: string; description: string; freshness_constant: number; relevance_constant: number; is_tldr_preview?: boolean }) => Promise<void>;
  onSetFeedEnabled?: (enabled: boolean) => Promise<void>;
  onFeedStatus: (message: string, isError?: boolean) => void;
  onSelectSavedSearch: (id: string) => void;
  /** Return to a blank, ad-hoc search composer. */
  onClearSearch: () => void;
  onPreviewTldrSearch?: (values: SearchSubmitValues) => void | Promise<void>;
  /** The window the run behind `elements` used, as that run reported it. */
  searchRunWindow?: SearchRunWindow | null;
}) {
  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [decay, setDecay] = useState(DEFAULT_DECAY);
  const [relevance, setRelevance] = useState(DEFAULT_RELEVANCE);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [feedSubmitting, setFeedSubmitting] = useState(false);
  const [searchWindowHours, setSearchWindowHours] = useState<24 | 72>(24);
  const [hasSearched, setHasSearched] = useState(false);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const [excludeCategories, setExcludeCategories] = useState<string[]>([]);
  const [readingMinutesMin, setReadingMinutesMin] = useState<number | null>(null);
  const [readingMinutesMax, setReadingMinutesMax] = useState<number | null>(null);
  const [tldrPreviewMode, setTldrPreviewMode] = useState(false);
  // Query expansion is an admin experiment, so it is off on every fresh
  // composer rather than remembered: a flag that persisted invisibly would
  // make two identical-looking searches retrieve differently.
  const [expandQuery, setExpandQuery] = useState(false);
  const [tldrPromptLoading, setTldrPromptLoading] = useState(false);
  const [tldrPromptLoadError, setTldrPromptLoadError] = useState('');
  const lastSyncedIdRef = useRef<string | null>(null);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [lastSearchSnapshot, setLastSearchSnapshot] = useState<string | null>(null);

  const navResetKey = isNew ? NEW_SEARCH_CHANNEL_ID : (savedSearch?.id ?? 'pending-search');
  // A 24h view of a 72h run is a display narrowing: the wider set stays in
  // `elements`, so switching back to 72h repopulates instantly with no re-run
  // (see selectSearchWindow). Every other case is the identity.
  const runWindow = searchRunWindow ?? null;
  const displayedElements = narrowToSelectedWindow(elements, runWindow, searchWindowHours);
  const {
    visibleElements,
    focusedIndex,
    expandedIds,
    focusStory,
    focusTopStory,
    toggleExpanded,
    setRowRef,
    notifyStoryUpdate,
  } = useStoryKeyboardNav(displayedElements, navResetKey, {
    surface: 'search',
    surfaceId: currentRunId ?? savedSearch?.id ?? navResetKey,
  });

  // A finished search hands the keyboard to its results. The composer's
  // textarea takes focus on mount and never gives it up, so without this the
  // first j/k/o after a run types into the query box instead of walking the
  // list the user just waited for.
  const wasPopulatingRef = useRef(populating);
  useEffect(() => {
    const justFinished = wasPopulatingRef.current && !populating;
    wasPopulatingRef.current = populating;
    if (!justFinished || error) return;
    focusTopStory();
  }, [populating, error, focusTopStory]);

  // Timed against what is on screen, not against `elements`: a 24h narrowing
  // of a 72h run paints the narrowed rows, and those are what the user waited
  // to see.
  const stopwatch = useSearchStopwatch({
    enabled: isAdmin,
    elementCount: displayedElements.length,
    populating,
    pollTimeline,
    timingDetails,
  });

  const showTldrPreviewControls = isAdmin && !!onPreviewTldrSearch;
  const showExpandQueryControl = isAdmin;
  const effectiveTldrPreviewMode = showTldrPreviewControls && tldrPreviewMode;
  const snapshotFor = (hours: 24 | 72) => searchSnapshot({ description, decay, relevance, excludeCategories, readingMinutesMin, readingMinutesMax, tldrPreviewMode: effectiveTldrPreviewMode, searchWindowHours: hours });
  const currentSnapshot = snapshotFor(searchWindowHours);
  // `isNew` is available immediately from the route, while the saved-search
  // definition may arrive asynchronously. Include it so the saved-search
  // picker does not briefly flash during navigation.
  const viewingSavedSearch = !isNew || !!savedSearch || !!runPrefill;
  // What's on screen is this query's own run, merely displayed narrower. No
  // fresh search would add anything, so the Search button must not light up as
  // if the query had changed.
  const servedByRunInHand = runWindow !== null
    && searchWindowHours < runWindow.hours
    && lastSearchSnapshot === snapshotFor(runWindow.hours as 24 | 72);
  const searchDirty = hasSearched && lastSearchSnapshot !== null
    && lastSearchSnapshot !== currentSnapshot && !servedByRunInHand;
  const searchIsFresh = hasSearched && !error
    && (lastSearchSnapshot === currentSnapshot || servedByRunInHand);
  const searchDisabled = !signedIn || submitting || !description.trim() || searchIsFresh;
  const clearDisabled = submitting || (
    !viewingSavedSearch
    && !hasSearched
    && !description
    && decay === DEFAULT_DECAY
    && relevance === DEFAULT_RELEVANCE
    && excludeCategories.length === 0
    && readingMinutesMin === null
    && readingMinutesMax === null
    && !tldrPreviewMode
    && searchWindowHours === 24
  );

  // Adopt the width of whatever run's results arrive, so the control states
  // the truth about what is on screen — a restored 72h run must not read
  // "24h". Keyed on the run's own identity, so a user narrowing to 24h does
  // not get snapped back: narrowing changes the selection, never the run.
  const adoptedRunRef = useRef<string | null>(null);
  useEffect(() => {
    if (!runWindow) return;
    const runKey = `${runWindow.startMs}:${runWindow.hours}`;
    if (adoptedRunRef.current === runKey) return;
    adoptedRunRef.current = runKey;
    setSearchWindowHours(runWindow.hours >= 72 ? 72 : 24);
  }, [runWindow?.startMs, runWindow?.hours]);

  // Entering new-search mode remounts this component (see composerEpoch key in
  // ReaderPage), so all form state resets to its initial value automatically —
  // no manual per-field reset to keep in sync as fields are added.

  useEffect(() => {
    descriptionRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [description]);

  useEffect(() => {
    if (!savedSearch) {
      lastSyncedIdRef.current = null;
      return;
    }
    if (savedSearch.id === lastSyncedIdRef.current) return;
    if (description.trim() && description.trim() === savedSearch.description.trim()) {
      lastSyncedIdRef.current = savedSearch.id;
      return;
    }
    lastSyncedIdRef.current = savedSearch.id;
    setDescription(savedSearch.description);
    setDecay(savedSearch.freshness_constant);
    setRelevance(savedSearch.relevance_constant);
    setName(savedSearch.name);
    setTldrPreviewMode(!!savedSearch.is_tldr_preview);
    setHasSearched(true);
    setExcludeCategories([]);
    setReadingMinutesMin(null);
    setReadingMinutesMax(null);
    // Selecting a saved search auto-runs it (ReaderPage), so these values are
    // the search already in flight — record them so an identical re-submit
    // while it runs is recognized as a duplicate (see submit()).
    setLastSearchSnapshot(searchSnapshot({
      description: savedSearch.description,
      decay: savedSearch.freshness_constant,
      relevance: savedSearch.relevance_constant,
      excludeCategories: [],
      readingMinutesMin: null,
      readingMinutesMax: null,
      tldrPreviewMode: isAdmin && !!onPreviewTldrSearch && !!savedSearch.is_tldr_preview,
    }));
  }, [savedSearch?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // A persisted run (e.g. a bookmarked run URL) prefills the composer with the
  // settings that produced it, so the search can be tweaked and re-run in place.
  useEffect(() => {
    if (!runPrefill) return;
    setDescription(runPrefill.description);
    setDecay(runPrefill.decay);
    setRelevance(runPrefill.relevance);
    setTldrPreviewMode(runPrefill.isTldrPreview);
    setHasSearched(true);
    setExcludeCategories([]);
    setReadingMinutesMin(null);
    setReadingMinutesMax(null);
    setLastSearchSnapshot(searchSnapshot({
      description: runPrefill.description,
      decay: runPrefill.decay,
      relevance: runPrefill.relevance,
      excludeCategories: [],
      readingMinutesMin: null,
      readingMinutesMax: null,
      tldrPreviewMode: isAdmin && !!onPreviewTldrSearch && runPrefill.isTldrPreview,
    }));
  }, [runPrefill?.searchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentFilters: SearchFilters = {
    excludeCategories,
    readingMinutesMin,
    readingMinutesMax,
  };

  /**
   * `clickTimeMs` is the originating event's `timeStamp`, passed through so
   * the stopwatch can start at the input rather than at this handler.
   */
  const submit = (windowHours?: 24 | 72, clickTimeMs?: number) => {
    if (!description.trim()) return;
    // The lookback toggle submits with the window it just selected, ahead of
    // the state update this render cannot see.
    const hours = windowHours ?? searchWindowHours;
    const snapshot = snapshotFor(hours);
    // An identical search is already running (page-load auto-run of a saved
    // search, or a repeated Enter): submitting again would start a duplicate
    // run judging the same candidates. Let the in-flight run finish; a changed
    // query or a re-submit after it finishes still goes through.
    if (populating && lastSearchSnapshot !== null && lastSearchSnapshot === snapshot) return;
    // Past every early return, so the clock only ever starts on a run that
    // actually starts. Inert unless the composer is an admin's (see the
    // hook's `enabled`).
    stopwatch.start(clickTimeMs);
    setHasSearched(true);
    setLastSearchSnapshot(snapshot);
    const values = {
      description: description.trim(),
      decay_exponent: decay,
      relevance_boost_multiplier: relevance,
      filters: currentFilters,
      fast_search_window_hours: hours,
      expand_query: showExpandQueryControl && expandQuery,
    };
    if (effectiveTldrPreviewMode) {
      onPreviewTldrSearch(values);
    } else {
      onSubmit(values);
    }
  };

  /**
   * Pick a lookback window.
   *
   * Narrowing (72h -> 24h) never re-runs: those results are a subset of what
   * the wider run already returned, so it is a display filter, and the wider
   * set is retained for an instant switch back. Widening (24h -> 72h) needs
   * candidates the narrower run never retrieved, so it searches immediately.
   *
   * Which of the two it is comes from the run's own reported window, not from
   * composer state: state resets on remount, and a reset made every flip look
   * like a narrowing of nothing, so both widths showed the same rows.
   */
  const selectSearchWindow = (hours: 24 | 72) => {
    if (hours === searchWindowHours) return;
    setSearchWindowHours(hours);
    if (searchWindowAction(runWindow, hours) !== 'search') return;
    if (!signedIn || submitting || !description.trim()) return;
    submit(hours);
  };

  const loadCurrentTldrPrompt = async () => {
    setTldrPromptLoading(true);
    setTldrPromptLoadError('');
    try {
      const prompt = await getTldrPrompt();
      setDescription(prompt.description ?? '');
      setDecay(prompt.decay_exponent ?? DEFAULT_DECAY);
      setRelevance(prompt.relevance_boost_multiplier ?? DEFAULT_RELEVANCE);
      setTldrPreviewMode(true);
      setHasSearched(false);
      setLastSearchSnapshot(null);
    } catch (e: unknown) {
      setTldrPromptLoadError(e instanceof Error ? e.message : 'Failed to load TLDR prompt');
    } finally {
      setTldrPromptLoading(false);
    }
  };

  const setFeedEnabled = async (enabled: boolean) => {
    setFeedSubmitting(true);
    try {
      if (isExistingSavedSearch && onSetFeedEnabled) {
        await onSetFeedEnabled(enabled);
      } else if (enabled) {
        await onSave({
          description: description.trim(),
          name: name.trim() || description.trim().slice(0, 40) || 'Search feed',
          decay_exponent: decay,
          relevance_boost_multiplier: relevance,
          filters: currentFilters,
          isTldrPreview: effectiveTldrPreviewMode,
          feedEnabled: true,
        });
      }
      onFeedStatus(enabled ? 'Feed enabled' : 'Feed stopped');
    } catch (e: unknown) {
      onFeedStatus(e instanceof Error ? e.message : 'Feed update failed', true);
    } finally {
      setFeedSubmitting(false);
    }
  };

  const copyFeedUrl = async () => {
    if (!savedSearch) return;
    await navigator.clipboard.writeText(`${window.location.origin}/feed/${savedSearch.id}`);
    onFeedStatus('Feed URL copied');
  };

  const scoringModified = decay !== DEFAULT_DECAY || relevance !== DEFAULT_RELEVANCE;

  const isExistingSavedSearch = !!savedSearch?.user_id && !!onUpdateSavedSearch;
  const savedSearchDirty = isExistingSavedSearch && (
    description.trim() !== savedSearch!.description.trim()
    || decay !== savedSearch!.freshness_constant
    || relevance !== savedSearch!.relevance_constant
    || (name.trim() !== '' && name.trim() !== savedSearch!.name.trim())
    || (showTldrPreviewControls && tldrPreviewMode !== !!savedSearch!.is_tldr_preview)
  );

  const updateSearch = async () => {
    if (!onUpdateSavedSearch || !savedSearch) return;
    setUpdateSubmitting(true);
    setUpdateError('');
    try {
      await onUpdateSavedSearch({
        name: name.trim() || savedSearch.name,
        description: description.trim(),
        freshness_constant: decay,
        relevance_constant: relevance,
        ...(showTldrPreviewControls ? { is_tldr_preview: tldrPreviewMode } : {}),
      });
    } catch (e: unknown) {
      setUpdateError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setUpdateSubmitting(false);
    }
  };

  const searchOptionsButton = (
    <button
      type="button"
      className={`search-pill search-options-pill${optionsOpen ? ' open' : ''}`}
      onClick={() => setOptionsOpen((v) => !v)}
      aria-expanded={optionsOpen}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }} aria-hidden="true">
        <path d={optionsOpen ? 'M2 6.5 L5 3.5 L8 6.5' : 'M2 3.5 L5 6.5 L8 3.5'} />
      </svg>
      Search options
      {scoringModified && <span className="search-options-modified-dot" title="Settings modified" aria-hidden="true" />}
    </button>
  );

  const clearSearch = () => {
    setDescription('');
    setDecay(DEFAULT_DECAY);
    setRelevance(DEFAULT_RELEVANCE);
    setExcludeCategories([]);
    setReadingMinutesMin(null);
    setReadingMinutesMax(null);
    setTldrPreviewMode(false);
    setSearchWindowHours(24);
    setHasSearched(false);
    setLastSearchSnapshot(null);
    setOptionsOpen(false);
    setName('');
    onClearSearch();
    descriptionRef.current?.focus();
  };

  return (
    <section className="feed-list-pane">
      <div className="feed-list-header">
        <div className="channel-heading">
          <div
            className="channel-heading-left"
            style={{ flex: '1 1 auto', width: '100%' }}
          >
            <div className="channel-heading-copy" style={{ width: '100%' }}>
              <div className="channel-heading-description-wrap" style={{ display: 'block' }}>
                <div className="channel-heading-settings" style={{ width: '100%', maxWidth: 'none' }}>
                  <div className="channel-setting">
                    <div className="search-composer-row">
                      <div className="search-composer-box">
                        <textarea
                          ref={descriptionRef}
                          className="search-composer-input"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows={1}
                          autoFocus
                          disabled={submitting}
                          placeholder="Describe what you're looking for..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (!e.shiftKey || e.ctrlKey)) {
                              e.preventDefault();
                              if (!searchDisabled) submit(undefined, e.timeStamp);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="search-composer-under">
                      {!signedIn && (
                        <span className="search-composer-signin-note">
                          <Link to="/login">Log in</Link> to run a search.
                        </span>
                      )}
                      <button
                        type="button"
                        className="search-submit"
                        onClick={(e) => submit(undefined, e.timeStamp)}
                        disabled={searchDisabled}
                      >
                        {submitting ? 'Searching...' : 'Search'}
                      </button>
                      <button
                        type="button"
                        className="search-pill search-clear-pill"
                        onClick={clearSearch}
                        disabled={clearDisabled}
                        aria-label={clearDisabled ? 'Nothing to clear' : 'Clear search'}
                        title={clearDisabled ? 'Nothing to clear' : 'Clear search and results'}
                      >
                        Clear
                      </button>
                      <div className="search-window-toggle" role="group" aria-label="Search time range">
                        {([24, 72] as const).map((hours) => (
                          <button
                            key={hours}
                            type="button"
                            className={searchWindowHours === hours ? 'active' : ''}
                            aria-pressed={searchWindowHours === hours}
                            onClick={() => selectSearchWindow(hours)}
                          >
                            {hours}h
                          </button>
                        ))}
                      </div>
                      {/* Admin mode only, and running whenever it is on: what
                          it measures is the real click-to-pixels wait, which
                          the server's timing waterfall structurally cannot
                          see. */}
                      {isAdmin && (
                        <SearchStopwatchFace
                          run={stopwatch.run}
                          history={stopwatch.history}
                          startedAtMs={stopwatch.startedAtMs}
                          running={stopwatch.running}
                        />
                      )}
                      {searchOptionsButton}
                      <TopSearchPills
                        compact
                        recentSearches={recentSearches}
                        topSearches={topSearches}
                        allSearches={allSearches}
                        onSelect={onSelectSavedSearch}
                      />
                      {shouldShowSearchFeedControls(signedIn, elements.length) && (
                        <div className="search-toolbar-save">
                          {!savedSearch?.feed_enabled ? (
                            <button
                              type="button"
                              className="search-save-trigger"
                              onClick={() => void setFeedEnabled(true)}
                              disabled={feedSubmitting}
                            >
                              {feedSubmitting ? 'Making feed...' : 'Make a feed'}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="search-save-trigger"
                              onClick={() => void copyFeedUrl()}
                            >
                              Copy feed URL
                            </button>
                          )}
                        </div>
                      )}
                      {isAdmin && hasSearched && !populating && elements.length > 0 && timingDetails && (
                        <SearchTimingWaterfall timing={timingDetails} compact />
                      )}
                      {/* Own isAdmin check: this results page is reachable by
                          non-admins, the audit route is not. Run-scoped, so the
                          link describes the run on screen rather than whatever
                          the search's latest run happens to be later. */}
                      {isAdmin && currentRunId && (
                        <Link className="search-pill fs-audit-link" to={`/search/${currentRunId}/audit`}>
                          Audit this run
                        </Link>
                      )}
                      {hasSearched && !populating && elements.length > 0 && isExistingSavedSearch && savedSearchDirty && (
                        <div className="search-toolbar-save">
                          <button
                            type="button"
                            className="primary-button"
                            onClick={updateSearch}
                            disabled={updateSubmitting}
                          >
                            {updateSubmitting ? 'Saving...' : 'Edit search'}
                          </button>
                          <button
                            type="button"
                            className="ghost-button"
                            onClick={() => {
                              setDescription(savedSearch!.description);
                              setDecay(savedSearch!.freshness_constant);
                              setRelevance(savedSearch!.relevance_constant);
                              setName(savedSearch!.name);
                              setTldrPreviewMode(!!savedSearch!.is_tldr_preview);
                            }}
                          >
                            Discard
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {optionsOpen && (
                    <div className="search-options-panel">
                    <div style={{ display: 'grid', gap: 16 }}>
                      {showTldrPreviewControls && (
                        <div>
                          <div className="search-toggle-action-row">
                            <label className="search-toggle-row">
                              <input
                                type="checkbox"
                                checked={tldrPreviewMode}
                                onChange={(e) => setTldrPreviewMode(e.target.checked)}
                                disabled={submitting || tldrPromptLoading}
                              />
                              <span>TLDR feed preview mode</span>
                            </label>
                            <button
                              type="button"
                              className="ghost-button"
                              onClick={loadCurrentTldrPrompt}
                              disabled={submitting || tldrPromptLoading}
                            >
                              {tldrPromptLoading ? 'Loading...' : 'Use current TLDR prompt'}
                            </button>
                          </div>
                          {tldrPromptLoadError && (
                            <div className="prompt-toast prompt-toast-error" style={{ marginTop: 8 }}>
                              {tldrPromptLoadError}
                            </div>
                          )}
                        </div>
                      )}
                      {showExpandQueryControl && (
                        <div>
                          <label className="search-toggle-row">
                            <input
                              type="checkbox"
                              checked={expandQuery}
                              onChange={(e) => setExpandQuery(e.target.checked)}
                              disabled={submitting}
                            />
                            <span>Expand query (LLM)</span>
                          </label>
                          <div className="search-toggle-hint">
                            Retrieves on generated sub-queries and keywords as well as the
                            brief itself. Helps briefs that describe a taste rather than a
                            subject; costs a second or two.
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="channel-setting-label">Recency decay</label>
                        <div className="channel-setting-slider-row">
                          <span className="channel-setting-endpoint">older</span>
                          <input
                            type="range"
                            min={DECAY_MIN}
                            max={DECAY_MAX}
                            step={DECAY_SLIDER_STEP}
                            value={decay}
                            onChange={(e) => setDecay(parseFloat(e.target.value))}
                            disabled={submitting}
                            style={{ flex: 1 }}
                          />
                          <span className="channel-setting-endpoint">recent</span>
                          <code className="channel-setting-value">{formatDecay(decay)}</code>
                          <button
                            type="button"
                            className="ghost-button channel-setting-reset"
                            onClick={() => setDecay(DEFAULT_DECAY)}
                            disabled={submitting}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="channel-setting-label">Relevance weighting</label>
                        <div className="channel-setting-slider-row">
                          <span className="channel-setting-endpoint">quality</span>
                          <input
                            type="range"
                            min={RELEVANCE_MULT_MIN}
                            max={RELEVANCE_MULT_MAX}
                            step={RELEVANCE_SLIDER_STEP}
                            value={relevance}
                            onChange={(e) => setRelevance(parseFloat(e.target.value))}
                            disabled={submitting}
                            style={{ flex: 1 }}
                          />
                          <span className="channel-setting-endpoint">match</span>
                          <code className="channel-setting-value">{formatRelevanceMultiplier(relevance)}</code>
                          <button
                            type="button"
                            className="ghost-button channel-setting-reset"
                            onClick={() => setRelevance(DEFAULT_RELEVANCE)}
                            disabled={submitting}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>

                    <CategoryFilterPills
                      excluded={excludeCategories}
                      onChange={setExcludeCategories}
                      disabled={submitting || effectiveTldrPreviewMode}
                    />

                    <ReadingTimeFilter
                      min={readingMinutesMin}
                      max={readingMinutesMax}
                      onChangeMin={setReadingMinutesMin}
                      onChangeMax={setReadingMinutesMax}
                      disabled={submitting || effectiveTldrPreviewMode}
                    />

                    {effectiveTldrPreviewMode && (
                      <div className="muted-text" style={{ fontSize: '0.75rem' }}>
                        Category and reading-time filters don&apos;t apply in TLDR feed preview mode — the main feed doesn&apos;t use them.
                      </div>
                    )}

                    {isExistingSavedSearch && (
                      <div style={{ marginTop: 12 }}>
                        <label className="channel-setting-label">Search name</label>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          disabled={submitting}
                          className="search-save-input"
                          style={{ width: '100%', boxSizing: 'border-box' }}
                        />
                      </div>
                    )}
                    </div>
                  )}

                  {hasSearched && (
                    <div className="search-results-area">
                      <div className="search-results-toolbar">
                      <span className="search-result-count">{elements.length}</span>
                      <span className="search-result-label">{populating ? searchingLabel(runProgress) : 'found'}</span>
                      {!populating && restoredAt && (
                        <span
                          className="search-result-label"
                          title="Showing this search's most recent results"
                        >
                          · from {formatAge(restoredAt)}
                        </span>
                      )}
                      {!populating && restoredAt && refreshingRestored && (
                        <span
                          className="search-result-label"
                          title="Searching articles ingested since these results"
                        >
                          · {refreshingLabel(runProgress)}
                        </span>
                      )}
                      {!populating && restoredAt && !refreshingRestored && onRerunRestored && (
                        <button
                          type="button"
                          className="search-deeper-inline"
                          onClick={onRerunRestored}
                          disabled={submitting}
                        >
                          Search again
                        </button>
                      )}
                      <ActiveFiltersStatus filters={currentFilters} />
                      </div>
                    </div>
                  )}


                  {error && <div className="prompt-toast prompt-toast-error">{error}</div>}
                  {updateError && <div className="prompt-toast prompt-toast-error" style={{ marginTop: 6 }}>{updateError}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="feed-list">
        {!hasSearched && visibleElements.length === 0 && !populating ? null : populating && visibleElements.length === 0 ? (
          <div className="empty-panel">Searching...</div>
        ) : visibleElements.length === 0 ? (
          <div className="empty-panel">No results yet.</div>
        ) : (
          visibleElements.map((story, i) => (
            <StoryRow
              key={story.id}
              story={story}
              isAdmin={isAdmin}
              isFocused={i === focusedIndex}
              rowRef={setRowRef(i)}
              isExpanded={expandedIds.has(story.id)}
              onFocusStory={focusStory}
              onToggleExpanded={toggleExpanded}
              onStoryUpdate={notifyStoryUpdate}
              eventContext={{ surface: 'search', surfaceId: currentRunId ?? savedSearch?.id ?? navResetKey }}
            />
          ))
        )}
      </div>
    </section>
  );
}

export default function ReaderPage() {
  const { user, logout, isLoggedIn, isAdmin } = useUser();
  // One id space: /search/<id> names a saved search, a bare description, or a
  // run, and the resolution effect below works out which by asking in turn.
  const { searchId } = useParams<{ searchId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Editing the search judge prompt lives on /admin/search-prompt, not in
  // the composer: it is a production prompt change, not a search control.

  const creatingSearch = searchId === 'new' || location.pathname === '/search/new';
  const urlSearchId = creatingSearch ? undefined : searchId;
  const selectedChannelId = urlSearchId ? savedSearchChannelId(urlSearchId) : (creatingSearch ? NEW_SEARCH_CHANNEL_ID : location.pathname === '/flagged' ? FLAGGED_CHANNEL_ID : TLDR_CHANNEL_ID);
  const showingComposer = creatingSearch || !!urlSearchId;

  const [elements, setElements] = useState<StoryElement[]>([]);
  const [loadingElements, setLoadingElements] = useState(!creatingSearch);
  // How deep the feed on screen currently is. Reset by every fresh load, so
  // switching channels always starts shallow again.
  const [feedDepth, setFeedDepth] = useState(channelRepository.READER_FEED_DEPTH);
  const [loadingMore, setLoadingMore] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [status, setStatus] = useState({ message: '', isError: false });
  const loadRequestRef = useRef(0);
  const [composerSaving, setComposerSaving] = useState(false);
  const [composerError, setComposerError] = useState('');
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [savedSearchesLoaded, setSavedSearchesLoaded] = useState(false);
  const [savedSearchesLoading, setSavedSearchesLoading] = useState(false);
  const [activeSavedSearch, setActiveSavedSearch] = useState<SavedSearch | null>(null);
  const [activeSearchTitle, setActiveSearchTitle] = useState<string | null>(null);
  const [runPrefill, setRunPrefill] = useState<RunPrefill | null>(null);
  const [currentSearchRunId, setCurrentSearchRunId] = useState<string | null>(null);
  // Whether the id in the URL turned out to name a search *definition* (saved
  // search or bare description) rather than a finished run. Both shapes share
  // /search/<id>, but they behave differently when a new run starts: a run URL
  // is a snapshot to leave behind, a definition URL is a page to stay on.
  const [urlIdIsDefinition, setUrlIdIsDefinition] = useState(false);
  const justNavigatedRef = useRef<string | null>(null);
  const [topSearches, setTopSearches] = useState<TopSavedSearch[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // When set, the results on screen were restored from a persisted prior run
  // instead of a live search: the timestamp feeds the "from Xm ago" note and
  // the ref holds the action that re-runs the same search live.
  const [restoredRunAt, setRestoredRunAt] = useState<string | null>(null);
  const rerunRestoredRef = useRef<(() => void) | null>(null);
  const markRestoredRun = useCallback((at: string | null, rerun: (() => void) | null) => {
    rerunRestoredRef.current = rerun;
    setRestoredRunAt(at);
  }, []);
  const clearRestoredRun = useCallback(() => {
    rerunRestoredRef.current = null;
    setRestoredRunAt(null);
  }, []);
  const handleRerunRestored = useCallback(() => {
    rerunRestoredRef.current?.();
  }, []);

  // Every run the pane starts (saved search, ad-hoc, TLDR preview, deepen)
  // streams its polls through this one follower. Progress and the poll
  // timeline update live, but fresh result sets commit only when their run is
  // finished: independently clustered intermediate snapshots can shrink as
  // later verdicts arrive, which made rows visibly appear and disappear.
  // A run that finished without judging every candidate keeps its results and
  // says nothing: incomplete coverage alarms server-side instead of putting a
  // red toast over results the reader can still use. Only a FAILED run throws.
  // The window the run behind `elements` retrieved under, as the run itself
  // reports it. The composer reads it to decide whether the 24h/72h control is
  // a display filter or a new search; keeping it here, beside `elements`,
  // is what stops the two from disagreeing (issue #885).
  const [searchRunWindow, setSearchRunWindow] = useState<SearchRunWindow | null>(null);
  const { runProgress, pollTimeline, timingDetails, beginRun, followRun } = useSearchRunFollower({
    onTitle: setActiveSearchTitle,
    onArticles: setElements,
    onRunWindow: setSearchRunWindow,
  });

  /**
   * Put the pane into its "run in flight" state. Every way of starting a run
   * goes through here so the reset can't drift between them. A retained view
   * keeps both its rows and their run window until the pending replacement is
   * ready; separating those would make the lookback control describe the
   * wrong result set.
   */
  const beginSearchRunView = useCallback(({
    keepElements = false,
    keepRunWindow = false,
  } = {}) => {
    clearRestoredRun();
    if (!keepElements) {
      setElements([]);
      setLoadingElements(false);
    }
    setPopulating(true);
    beginRun({ keepRunWindow });
    setStatus({ message: '', isError: false });
  }, [clearRestoredRun, beginRun]);

  // True while a restored run's automatic incremental refresh is in flight;
  // the restored results stay on screen until the refresh commits.
  const [refreshingRestored, setRefreshingRestored] = useState(false);

  // Force a clean SearchPane remount whenever we *enter* new-search mode, so its
  // form state can't leak in from a previously-composed search. We key on this
  // epoch rather than on the URL because submitting a new search navigates
  // /search/new -> /search/<id> (creatingSearch true -> false), and
  // that transition must NOT remount the pane — only arriving back at new-search
  // mode should. Incrementing solely on the false->true edge captures exactly that.
  const [composerEpoch, setComposerEpoch] = useState(0);
  const prevCreatingSearchRef = useRef(creatingSearch);
  if (creatingSearch && !prevCreatingSearchRef.current) {
    setComposerEpoch((n) => n + 1);
  }
  prevCreatingSearchRef.current = creatingSearch;

  useEffect(() => {
    // Search feeds are per-account, so an anonymous visitor has none to
    // fetch: asking would 401 and bounce them out of the logged-out composer.
    if (showingComposer && isLoggedIn) {
      getTopSavedSearches().then(setTopSearches).catch(() => {});
      listRecentSearches().then(setRecentSearches).catch(() => {});
      if (!savedSearchesLoaded && !savedSearchesLoading) {
        loadSavedSearches();
      }
    }
  }, [showingComposer]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSavedSearches = useCallback(async () => {
    if (savedSearchesLoaded || savedSearchesLoading) {
      return savedSearches;
    }
    setSavedSearchesLoading(true);
    try {
      const descriptions = await listSavedSearches();
      setSavedSearches(descriptions);
      setSavedSearchesLoaded(true);
      if (urlSearchId) {
        const selected = descriptions.find((feed) => feed.id === urlSearchId);
        if (selected) setActiveSavedSearch(selected);
      }
      return descriptions;
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load feeds', isError: true });
      setSavedSearches([]);
      setSavedSearchesLoaded(true);
      return [];
    } finally {
      setSavedSearchesLoading(false);
    }
  }, [savedSearches, savedSearchesLoaded, savedSearchesLoading, urlSearchId]);

  // Admin mode reads live, but the fetch it triggers must not. Every load
  // starts in the reader's view (see UserProvider), so this effect's guard is
  // false on the first pass and the feed's first paint asks the server for
  // neither the admin score overlay nor the per-user status flags -- the two
  // layers that cost a signed-in reader a database round trip apiece for
  // chrome their view does not draw. Switching admin mode on is what fetches
  // them, once per channel, with the rows already on screen kept in place
  // behind a spinner.
  const [adminDataLoading, setAdminDataLoading] = useState(false);
  const adminHydratedRef = useRef<string | null>(null);
  // Read by the load callbacks, which must stay stable across an admin-mode
  // flip: a dependency on `isAdmin` would make them re-run the feed load.
  const isAdminRef = useRef(isAdmin);
  isAdminRef.current = isAdmin;

  useEffect(() => {
    if (!isAdmin || showingComposer) return;
    if (adminHydratedRef.current === selectedChannelId) return;
    // Not a new load: the rows on screen stay, so any load already in flight
    // keeps its claim and this fetch simply drops if one starts or finishes
    // after it.
    const loadAt = loadRequestRef.current;
    setAdminDataLoading(true);
    channelRepository.listElements(selectedChannelId, feedDepth, { admin: true })
      .then((els) => {
        if (loadRequestRef.current !== loadAt) return;
        adminHydratedRef.current = selectedChannelId;
        setElements(els);
      })
      .catch((err) => {
        if (loadRequestRef.current !== loadAt) return;
        setStatus({ message: (err as Error).message || 'Failed to load admin data', isError: true });
      })
      // Unguarded, unlike the two above: a superseded fetch must still take its
      // spinner down, or a channel switch mid-flight would leave one spinning
      // over rows nothing is loading for.
      .finally(() => setAdminDataLoading(false));
    return () => setAdminDataLoading(false);
  }, [isAdmin, showingComposer, selectedChannelId, feedDepth]);

  const loadElements = useCallback(async (
    channelId: string,
    options: { preserveExisting?: boolean } = {},
  ) => {
    const requestId = ++loadRequestRef.current;
    setLoadingElements(true);
    if (!options.preserveExisting) {
      setElements([]);
    }
    setCurrentSearchRunId(null);
    setActiveSearchTitle(null);
    setSearchRunWindow(null);
    clearRestoredRun();
    rememberSelectedChannel(channelId);
    adminHydratedRef.current = isAdminRef.current ? channelId : null;
    try {
      const els = await channelRepository.listElements(channelId, channelRepository.READER_FEED_DEPTH, { admin: isAdminRef.current });
      if (loadRequestRef.current !== requestId) return;
      setElements(els);
      setFeedDepth(channelRepository.READER_FEED_DEPTH);
    } catch (err) {
      if (loadRequestRef.current !== requestId) return;
      setStatus({ message: (err as Error).message || 'Failed to load articles', isError: true });
    } finally {
      if (loadRequestRef.current !== requestId) return;
      setLoadingElements(false);
    }
  }, [clearRestoredRun]);

  /**
   * Deepen the feed to its full depth. Refetches rather than appending: the
   * list has no paging cursor, and the deeper response is the same snapshot's
   * ordered prefix plus the rest, so replacing it keeps the rows a reader has
   * already scrolled past exactly where they were.
   *
   * Deliberately not routed through loadElements, which raises the pane-level
   * loading flag -- that would blank the list a reader is looking at in order
   * to tell them more of it is coming.
   */
  const loadMoreElements = useCallback(async (channelId: string) => {
    const requestId = ++loadRequestRef.current;
    setLoadingMore(true);
    try {
      const els = await channelRepository.listElements(
        channelId,
        channelRepository.READER_FEED_FULL_DEPTH,
        { admin: isAdminRef.current },
      );
      if (loadRequestRef.current !== requestId) return;
      setElements(els);
      setFeedDepth(channelRepository.READER_FEED_FULL_DEPTH);
    } catch (err) {
      if (loadRequestRef.current !== requestId) return;
      setStatus({ message: (err as Error).message || 'Failed to load more articles', isError: true });
    } finally {
      if (loadRequestRef.current === requestId) setLoadingMore(false);
    }
  }, []);

  // The control shows only while there is plausibly more to fetch: a feed that
  // came back short of the shallow depth is already complete, and one that has
  // been deepened has nowhere further to go.
  const canDeepenFeed =
    feedDepth === channelRepository.READER_FEED_DEPTH
    && elements.length >= channelRepository.READER_FEED_DEPTH;

  const runSavedSearchByPayload = useCallback(async function runPayload(
    payload: Parameters<typeof runSearch>[0],
    feedId: string,
    description?: SavedSearch,
  ) {
    const requestId = ++loadRequestRef.current;

    rememberSelectedSavedSearch(feedId);
    if (description) setActiveSavedSearch(description);
    setActiveSearchTitle(description?.name ?? null);
    setRunPrefill(null);
    beginSearchRunView();

    try {
      const started = await runSearch(payload);
      setCurrentSearchRunId(started.search_id);
      if (started.title) setActiveSearchTitle(started.title);
      // The server may answer with a prior run's results: outright (a young
      // run — same search_id) or as the seed of an incremental refresh (new
      // run whose first polls already carry the prior articles). Label them
      // with their age; "Search again" forces a full live run.
      const restoredFrom = started.restored_from ?? null;
      const incremental = restoredFrom != null && restoredFrom.search_id !== started.search_id;
      if (restoredFrom) {
        markRestoredRun(restoredFrom.finished_at, () => {
          void runPayload({ ...payload, force: true }, feedId, description);
        });
        setRefreshingRestored(incremental);
      }
      const finished = await followRun({
        fetchStatus: () => getSearchRunStatus(started.search_id),
        isCurrent: () => loadRequestRef.current === requestId,
        errorLabel: 'Saved search failed',
        // A restored run's first snapshot is already a stable prior result set
        // and should paint immediately while an incremental refresh proceeds.
        // A fresh run streams instead: its cached verdicts are published by the
        // server before the judge wave is even dispatched, so waiting for
        // FINISHED left a warm search blank for seconds with the answer already
        // fetched and discarded.
        publishResults: restoredFrom ? 'each-poll' : 'streaming',
        // A restored response is complete from the first poll, so drop the
        // searching state immediately and let the age label carry the story.
        onPoll: () => { if (restoredFrom) setPopulating(false); },
      });
      if (finished && incremental) clearRestoredRun();
    } catch (err) {
      if (loadRequestRef.current === requestId) {
        setStatus({ message: (err as Error).message || 'Failed to search smart feed', isError: true });
      }
    } finally {
      if (loadRequestRef.current === requestId) {
        setPopulating(false);
        setRefreshingRestored(false);
      }
    }
  }, [beginSearchRunView, clearRestoredRun, getSearchRunStatus, followRun, markRestoredRun, runSearch]);

  const pollTldrPreviewRun = useCallback((searchId: string, requestId: number) => followRun({
    fetchStatus: () => getTldrPreviewStatus(searchId),
    isCurrent: () => loadRequestRef.current === requestId,
    errorLabel: 'TLDR preview failed',
  }), [followRun]);

  const runTldrPreviewForSavedSearch = useCallback(async (description: SavedSearch) => {
    const requestId = ++loadRequestRef.current;
    rememberSelectedSavedSearch(description.id);
    setActiveSavedSearch(description);
    setActiveSearchTitle(description.name);
    setRunPrefill(null);
    beginSearchRunView();
    try {
      const started = await startTldrPreview({
        description: description.description,
        decay_exponent: description.freshness_constant,
        relevance_boost_multiplier: description.relevance_constant,
      });
      setCurrentSearchRunId(started.search_id);
      await pollTldrPreviewRun(started.search_id, requestId);
    } catch (err) {
      if (loadRequestRef.current === requestId) {
        setStatus({ message: (err as Error).message || 'TLDR preview failed', isError: true });
      }
    } finally {
      if (loadRequestRef.current === requestId) {
        setPopulating(false);
      }
    }
  }, [pollTldrPreviewRun, beginSearchRunView]);

  const runSavedSearchDefinition = useCallback(async (description: SavedSearch) => {
    setSavedSearches((prev) => (
      prev.some((feed) => feed.id === description.id)
        ? prev.map((feed) => (feed.id === description.id ? description : feed))
        : [description, ...prev]
    ));
    if (description.is_tldr_preview && isAdmin) {
      await runTldrPreviewForSavedSearch(description);
      return;
    }
    await runSavedSearchByPayload({
      description: description.description,
      relevance_constant: description.relevance_constant,
      freshness_constant: description.freshness_constant,
    }, description.id, description);
  }, [runSavedSearchByPayload, runTldrPreviewForSavedSearch, isAdmin]);

  const loadSavedSearchRun = useCallback(async (searchRunId: string): Promise<boolean> => {
    try {
      let status;
      if (isAdmin) {
        try {
          status = await getTldrPreviewStatus(searchRunId);
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
      status = status ?? await getSearchRunStatus(searchRunId);
      setActiveSavedSearch(null);
      setActiveSearchTitle(status.title ?? null);
      setCurrentSearchRunId(searchRunId);
      setElements(status.articles);
      // A restored run reports its own window like any other, so a bookmarked
      // 72h run URL still narrows to 24h on the client instead of re-running.
      setSearchRunWindow(runWindowFromStatus(status));
      setLoadingElements(false);
      setPopulating(false);
      // A run URL always shows persisted results; surface their age and offer
      // a live re-run (except TLDR previews, which re-run through their own
      // admin flow). force skips the server's reuse policy — a permalink
      // rerun would otherwise just get this same run handed back.
      const isTldrPreviewRun = (status.title || '').startsWith('TLDR Preview');
      if (!isTldrPreviewRun && isRunFinished(status.status) && status.articles.length > 0) {
        const { description_id: rerunDescriptionId, relevance, freshness } = status;
        markRestoredRun(status.finished_at ?? status.created_at ?? null, () => {
          void runSavedSearchByPayload({
            description_id: rerunDescriptionId,
            relevance_constant: relevance,
            freshness_constant: freshness,
            force: true,
          }, rerunDescriptionId);
        });
      } else {
        clearRestoredRun();
      }
      try {
        const desc = await getSearchDescription(status.description_id);
        setRunPrefill({
          searchId: searchRunId,
          description: desc.description,
          decay: status.freshness,
          relevance: status.relevance,
          isTldrPreview: (status.title || '').startsWith('TLDR Preview'),
        });
      } catch {
        setStatus({ message: 'Failed to load this run\'s search prompt', isError: true });
      }
      return true;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        return false;
      }
      throw err;
    }
  }, [isAdmin, markRestoredRun, clearRestoredRun, getSearchRunStatus, runSavedSearchByPayload]);

  useEffect(() => {
    if (creatingSearch) {
      setActiveSavedSearch(null);
      setActiveSearchTitle(null);
      setCurrentSearchRunId(null);
      setRunPrefill(null);
      clearRestoredRun();
      setElements([]);
      setSearchRunWindow(null);
      setLoadingElements(false);
      return;
    }

    if (urlSearchId && justNavigatedRef.current === urlSearchId) {
      justNavigatedRef.current = null;
      rememberSelectedSavedSearch(urlSearchId);
      return;
    }
    justNavigatedRef.current = null;

    if (urlSearchId) {
      rememberSelectedSavedSearch(urlSearchId);
      // Clear the previous search's results synchronously, before the awaits below, so
      // they don't flash on screen while the saved search loads and the new run starts.
      setElements([]);
      setPopulating(true);
      beginRun();
      (async () => {
        try {
          // Saved search, then run, then bare description: the three things an
          // id can name, asked in the order that costs the fewest requests for
          // the common case (a saved search the reader navigated to).
          let savedSearch: SavedSearch | null = null;
          try {
            savedSearch = await getSavedSearch(urlSearchId);
          } catch (err) {
            if (!(err instanceof ApiError && err.status === 404)) throw err;
          }
          if (savedSearch) {
            setUrlIdIsDefinition(true);
            await runSavedSearchDefinition(savedSearch);
          } else {
            const descriptions = await loadSavedSearches();
            const savedFromList = descriptions.find((feed) => feed.id === urlSearchId);
            if (savedFromList) {
              setUrlIdIsDefinition(true);
              await runSavedSearchDefinition(savedFromList);
              return;
            }
            const loadedRun = await loadSavedSearchRun(urlSearchId);
            if (loadedRun) {
              setUrlIdIsDefinition(false);
            } else {
              setUrlIdIsDefinition(true);
              getSearchDescription(urlSearchId).then((desc) => {
                setActiveSavedSearch({
                  id: urlSearchId,
                  user_id: '',
                  name: desc.description.slice(0, 80),
                  description: desc.description,
                  description_hash: '',
                  relevance_constant: SAVED_SEARCH_DEFAULT_RELEVANCE,
                  freshness_constant: SAVED_SEARCH_DEFAULT_DECAY,
                  sort_order: 0,
                  feed_enabled: false,
                  in_for_you: false,
                  archived_at: null,
                  created_at: '',
                  updated_at: '',
                });
              }).catch(() => {});
              // Constants unpinned: the server's reuse policy hands back or
              // seeds from the newest run whatever constants it used.
              await runSavedSearchByPayload({ description_id: urlSearchId }, urlSearchId);
            }
          }
        } catch (err) {
          setStatus({ message: (err as Error).message || 'Failed to load reader', isError: true });
          setPopulating(false);
        }
      })();
    } else {
      rememberSelectedChannel(TLDR_CHANNEL_ID);
      loadElements(TLDR_CHANNEL_ID);
    }
  }, [searchId, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectChannel = useCallback((channelId: string) => {
    const ssId = savedSearchIdFromChannel(channelId);
    if (ssId) {
      navigate(`/search/${ssId}`);
    } else {
      navigate('/tldr');
    }
  }, [navigate]);

  const handleSearchSubmit = async ({
    description,
    decay_exponent,
    relevance_boost_multiplier,
    filters,
    fast_search_window_hours,
    expand_query,
  }: SearchSubmitValues) => {
    if (!description) {
      setComposerError('Description is required');
      return;
    }
    setComposerSaving(true);
    setComposerError('');
    const wideningSearch = searchRunWindow !== null
      && fast_search_window_hours !== undefined
      && fast_search_window_hours > searchRunWindow.hours;
    beginSearchRunView({
      keepElements: wideningSearch,
      keepRunWindow: wideningSearch,
    });
    const requestId = ++loadRequestRef.current;
    try {
      const started = await runSearch({
        description,
        relevance_constant: relevance_boost_multiplier,
        freshness_constant: decay_exponent,
        exclude_categories: filters.excludeCategories.length > 0 ? filters.excludeCategories : undefined,
        reading_minutes_min: filters.readingMinutesMin ?? undefined,
        reading_minutes_max: filters.readingMinutesMax ?? undefined,
        fast_search_window_hours,
        expand_query,
      });
      setCurrentSearchRunId(started.search_id);
      if (started.title) setActiveSearchTitle(started.title);
      // Re-searching a recently run description gets the server's reuse
      // treatment: a young run handed back outright, or an incremental run
      // seeded with the prior results (see runSavedSearchByPayload).
      const restoredFrom = started.restored_from ?? null;
      const incremental = restoredFrom != null && restoredFrom.search_id !== started.search_id;
      if (restoredFrom) {
        markRestoredRun(restoredFrom.finished_at, () => {
          void runSavedSearchByPayload({
            description_id: started.description_id,
            relevance_constant: relevance_boost_multiplier,
            freshness_constant: decay_exponent,
            force: true,
            // Carried through: this is the same search the reader just asked
            // for, refreshed. Dropping the flag here would silently answer an
            // expanded request with an unexpanded run.
            expand_query,
          }, started.description_id);
        });
        setRefreshingRestored(incremental);
      }
      const finished = await followRun({
        fetchStatus: () => getSearchRunStatus(started.search_id),
        isCurrent: () => loadRequestRef.current === requestId,
        errorLabel: 'Search failed',
        // Fresh intermediate snapshots may lose rows as topic clusters merge,
        // so they stream additively rather than verbatim: rows accumulate as
        // verdicts land, and the finished snapshot settles any merge.
        publishResults: restoredFrom ? 'each-poll' : 'streaming',
        onPoll: () => { if (restoredFrom) setPopulating(false); },
      });
      if (finished) {
        if (incremental) clearRestoredRun();
        rememberSelectedSavedSearch(started.description_id);
        setUrlIdIsDefinition(true);
        justNavigatedRef.current = started.description_id;
        navigate(`/search/${started.description_id}`, { replace: true });
        listRecentSearches().then(setRecentSearches).catch(() => {});
      }
    } catch (e: unknown) {
      setComposerError(e instanceof Error ? e.message : 'Search failed');
      setPopulating(false);
    } finally {
      setComposerSaving(false);
      if (loadRequestRef.current === requestId) {
        setPopulating(false);
        setRefreshingRestored(false);
      }
    }
  };

  const handleTldrPreviewSubmit = async ({
    description,
    decay_exponent,
    relevance_boost_multiplier,
  }: SearchSubmitValues) => {
    if (!description) {
      setComposerError('Description is required');
      return;
    }
    setComposerSaving(true);
    setComposerError('');
    beginSearchRunView();
    const requestId = ++loadRequestRef.current;
    try {
      const started = await startTldrPreview({
        description,
        decay_exponent,
        relevance_boost_multiplier,
      });
      setCurrentSearchRunId(started.search_id);
      if (started.title) setActiveSearchTitle(started.title);
      const finished = await pollTldrPreviewRun(started.search_id, requestId);
      // Land on the run's durable URL so the result can be bookmarked/shared.
      // justNavigatedRef tells the URL effect the results are already on screen,
      // so it must not clear and refetch them. From a saved-search page, stay put:
      // the run is persisted and the page keeps its save/update controls.
      if (finished && !urlIdIsDefinition) {
        rememberSelectedSavedSearch(started.search_id);
        setUrlIdIsDefinition(false);
        justNavigatedRef.current = started.search_id;
        navigate(`/search/${started.search_id}`, { replace: true });
      }
    } catch (e: unknown) {
      setComposerError(e instanceof Error ? e.message : 'TLDR preview failed');
      setStatus({ message: e instanceof Error ? e.message : 'TLDR preview failed', isError: true });
      setPopulating(false);
    } finally {
      setComposerSaving(false);
      if (loadRequestRef.current === requestId) {
        setPopulating(false);
      }
    }
  };

  const handleSearchSave = async ({
    description,
    name,
    decay_exponent,
    relevance_boost_multiplier,
    isTldrPreview,
    feedEnabled,
  }: SearchSaveValues) => {
    if (!description) {
      throw new Error('Description is required');
    }
    const saved = await createSavedSearch({
      name: name || description.slice(0, 40) || 'Saved search',
      description,
      relevance_constant: relevance_boost_multiplier,
      freshness_constant: decay_exponent,
      is_tldr_preview: isTldrPreview,
      feed_enabled: feedEnabled,
    });
    setSavedSearches((prev) => [saved, ...prev.filter((feed) => feed.id !== saved.id)]);
    setSavedSearchPresence(true);
    setSavedSearchesLoaded(true);
    setActiveSavedSearch(saved);
    setUrlIdIsDefinition(true);
    justNavigatedRef.current = saved.id;
    navigate(`/search/${saved.id}`, { replace: true });
    setStatus({ message: 'Search saved', isError: false });
  };

  const handleSearchPaneUpdate = useCallback(async (settings: {
    name: string;
    description: string;
    freshness_constant: number;
    relevance_constant: number;
    is_tldr_preview?: boolean;
  }) => {
    if (!activeSavedSearch) return;
    if (!settings.description) throw new Error('Description is required');
    const updated = await updateSavedSearch(activeSavedSearch.id, {
      name: settings.name || settings.description.slice(0, 40) || 'Saved search',
      description: settings.description,
      freshness_constant: settings.freshness_constant,
      relevance_constant: settings.relevance_constant,
      is_tldr_preview: settings.is_tldr_preview,
    });
    setActiveSavedSearch(updated);
    setSavedSearches((prev) => [updated, ...prev.filter((feed) => feed.id !== updated.id)]);
    setSavedSearchesLoaded(true);
    setStatus({ message: 'Search settings saved', isError: false });
  }, [activeSavedSearch]);

  const handleSetFeedEnabled = useCallback(async (enabled: boolean) => {
    if (!activeSavedSearch) return;
    const updated = await updateSavedSearch(activeSavedSearch.id, { feed_enabled: enabled });
    setActiveSavedSearch(updated);
    setSavedSearches((prev) => [updated, ...prev.filter((feed) => feed.id !== updated.id)]);
    setSavedSearchesLoaded(true);
    setStatus({ message: enabled ? 'Feed enabled' : 'Feed stopped', isError: false });
  }, [activeSavedSearch]);

  const currentSavedSearch = activeSavedSearch?.id === urlSearchId ? activeSavedSearch : null;
  const currentRunIsTldrPreview = (activeSearchTitle || '').startsWith('TLDR Preview');

  const dismissStatus = useCallback(() => setStatus({ message: '', isError: false }), []);

  return (
    <div className="reader-shell">
      <Toast
        message={status.message}
        isError={status.isError}
        duration={status.message === 'Feed URL copied' ? 3000 : 5000}
        onDismiss={dismissStatus}
      />
      <ReaderNavbar
        selectedChannelId={selectedChannelId}
        onSelectChannel={handleSelectChannel}
        user={user}
        onLogout={logout}
      />
      {!showingComposer && <TldrSubscribeBand />}
      <main className="reader-main-full">
        {showingComposer ? (
          <SearchPane
            key={`composer-${composerEpoch}`}
            elements={elements}
            populating={populating}
            submitting={composerSaving}
            error={composerError}
            isAdmin={isAdmin}
            signedIn={isLoggedIn}
            isNew={creatingSearch}
            topSearches={topSearches}
            allSearches={savedSearches}
            recentSearches={recentSearches}
            savedSearch={currentSavedSearch}
            runPrefill={runPrefill}
            restoredAt={restoredRunAt}
            refreshingRestored={refreshingRestored}
            runProgress={runProgress}
            pollTimeline={pollTimeline}
            timingDetails={timingDetails}
            currentRunId={currentSearchRunId}
            onRerunRestored={handleRerunRestored}
            onSubmit={handleSearchSubmit}
            onSave={handleSearchSave}
            onUpdateSavedSearch={currentSavedSearch ? handleSearchPaneUpdate : undefined}
            onSetFeedEnabled={currentSavedSearch ? handleSetFeedEnabled : undefined}
            onFeedStatus={(message, isError = false) => setStatus({ message, isError })}
            onSelectSavedSearch={(id) => navigate(`/search/${id}`)}
            onClearSearch={() => {
              ++loadRequestRef.current;
              setElements([]);
              setSearchRunWindow(null);
              setCurrentSearchRunId(null);
              setActiveSavedSearch(null);
              setRunPrefill(null);
              clearRestoredRun();
              setPopulating(false);
              setComposerEpoch((n) => n + 1);
              navigate('/search/new');
            }}
            onPreviewTldrSearch={isAdmin ? handleTldrPreviewSubmit : undefined}
            searchRunWindow={searchRunWindow}
          />
        ) : (
          <StoryList
            selectedChannelId={selectedChannelId}
            elements={elements}
            loading={loadingElements}
            populating={populating}
            isAdmin={isAdmin}
            adminLoading={adminDataLoading}
            onLoadMore={canDeepenFeed ? () => loadMoreElements(selectedChannelId) : undefined}
            loadingMore={loadingMore}
          />
        )}
      </main>
      <ReaderFooter />
    </div>
  );
}
